'use client';

import { useState, useMemo, useCallback } from 'react';
import { Table, TableBody, TableContainer, Paper, Typography } from '@mui/material';
import { sortData } from '../../../utils/tableUtils';
import SortableTableHeader from './SortableTableHeader';
import SortableTableRow from './SortableTableRow';

/**
 * Reusable sortable data table component
 *
 * @param {Array} columns - Column definitions with { field, label, type, sortable, align, maxWidth, render }
 * @param {Array} data - Array of row data objects
 * @param {Function} onRowClick - Callback when row is clicked
 * @param {Function} onDownload - Optional callback for download action (shown for DELIVERED status)
 * @param {string} initialSortField - Initial sort field (default: 'parcelCreatedAt')
 * @param {string} initialSortDirection - Initial sort direction (default: 'desc')
 * @param {string} emptyMessage - Message to show when no data
 */
export default function SortableDataTable({
  columns,
  data,
  onRowClick,
  onDownload,
  initialSortField = 'parcelCreatedAt',
  initialSortDirection = 'desc',
  emptyMessage = 'No data available',
}) {
  const [sortField, setSortField] = useState(initialSortField);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);

  const handleSort = useCallback(
    (field) => {
      if (sortField === field) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDirection('desc');
      }
    },
    [sortField]
  );

  const sortedData = useMemo(() => {
    return sortData(data, sortField, sortDirection);
  }, [data, sortField, sortDirection]);

  if (!data || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}
    >
      <Table size="small">
        <SortableTableHeader
          columns={columns}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
        <TableBody>
          {sortedData.map((row, idx) => (
            <SortableTableRow
              key={row.parcelId || row.id || idx}
              row={row}
              columns={columns}
              onRowClick={onRowClick}
              onDownload={onDownload}
              index={idx}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
